import { type Period, type ProductView } from '@heidi/commerce/contracts';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useIntl } from 'react-intl';
import type {
  PlanPriceDisplayCopyOverrides,
  PlanPriceDisplayProps,
} from '../../../../trials-and-upgrades/plan-price-display/types';
import type { CommerceBillingUiPorts } from '../../billing-ui-ports';
import { getCommerceBillingUiPorts } from '../../billing-ui-ports';
import { pricingPlansModalMessages } from './pricing-plans-modal.messages';

const CLINICIAN_MAX_PRODUCT_NAME = 'Clinician Max';

/** The `*` defers to the modal's sales-tax footnote, so no second caption line. */
const DEFAULT_COPY: PlanPriceDisplayCopyOverrides = {
  perUserMonth: pricingPlansModalMessages.perUserMonth,
  excludeGst: false,
};

function resolveCatalogueProduct({
  tier,
  products,
  seatType,
}: {
  tier: SubscriptionTier;
  products?: ProductView[];
  seatType?: BillingSeatType;
}): ProductView | undefined {
  if (seatType === BillingSeatType.MAX) {
    // v3 states the seat, so tier + seat is the row's unique key. v2 rows carry
    // no `seat_type`, so the beta's Clinician Max product is still only
    // findable by name. Practice Max exists on v3 only — never fall through to
    // the Clinician Max row for it.
    return (
      getCommerceBillingUiPorts().products.getNewProductTierProduct(
        tier,
        products,
        BillingSeatType.MAX
      ) ??
      (tier === SubscriptionTier.CLINICIAN_PRO
        ? getCommerceBillingUiPorts().products.getProductByName(
            products,
            CLINICIAN_MAX_PRODUCT_NAME
          )
        : undefined)
    );
  }

  return (
    getCommerceBillingUiPorts().products.getNewProductTierProduct(tier, products) ??
    (tier === SubscriptionTier.PRO
      ? getCommerceBillingUiPorts().products.getTierProduct(tier, products)
      : undefined)
  );
}

export type PlanPriceDisplayResult = PlanPriceDisplayProps & {
  product?: ProductView;
  plan?: ReturnType<CommerceBillingUiPorts['products']['getPlanFromPeriod']>;
  monthlyPrice?: string;
  annualPrice?: string;
  annualSavePercent?: number;
  isPending: boolean;
  isError: boolean;
};

/**
 * Catalogue product + per-month `PlanPriceDisplay` props. `seatType: MAX` shares the tier of the
 * standard seat, so it is resolved by seat (v3) or by name (v2) rather than by tier alone.
 * Free/Enterprise (and a paid tier with no period price) skip per-user/billed-annually captions.
 */
export function usePlanPriceDisplay({
  tier,
  period,
  seatType,
}: {
  tier: SubscriptionTier;
  period: Period;
  seatType?: BillingSeatType;
}): PlanPriceDisplayResult {
  const { locale, formatMessage } = useIntl();
  // Both seats of these tiers, Max included, are rows on the Heidi 2 catalogue.
  const isHeidi2CatalogueTier =
    tier === SubscriptionTier.CLINICIAN_PRO ||
    tier === SubscriptionTier.CLINICIAN_PRO_PLUS;
  const {
    data: products,
    isPending,
    isError,
  } = getCommerceBillingUiPorts().products.useQueryProducts({
    heidi2Catalogue: isHeidi2CatalogueTier,
  });
  const product = resolveCatalogueProduct({ tier, products, seatType });
  const plan = getCommerceBillingUiPorts().products.getPlanFromPeriod(period, product);
  const { monthlyPrice, annualPrice, annualSavePercent } =
    getCommerceBillingUiPorts().products.getProductUpgradeDisplayPrices(product, locale);
  const catalogue = {
    product,
    plan,
    monthlyPrice,
    annualPrice,
    annualSavePercent,
    isPending,
    isError,
  };

  if (tier === SubscriptionTier.ENTERPRISE) {
    return {
      price: formatMessage(pricingPlansModalMessages.customPrice),
      locale,
      copy: false,
      ...catalogue,
    };
  }

  if (tier === SubscriptionTier.FREE) {
    const currencyCode = plan?.currency ?? '';
    return {
      price: getCommerceBillingUiPorts().products.formatPriceWithCurrency(
        '0',
        currencyCode,
        getCommerceBillingUiPorts().products.getCurrencySymbol(currencyCode)
      ),
      locale,
      copy: false,
      ...catalogue,
    };
  }

  return {
    price:
      getCommerceBillingUiPorts().products.getDisplayedPriceFromPlan(
        plan,
        'month',
        false,
        locale
      ) ?? '—',
    locale,
    copy: plan ? DEFAULT_COPY : false,
    ...catalogue,
  };
}
